/* eslint-disable */
// Store settings (Store.config) on the edit-store page. Each change saves at once and is logged
// with the user (console2Backend GET/POST /v2.1/store/config).
import { useEffect, useState } from 'react';
import moment from 'moment';
import { Switch } from '@mui/material';
import { notificationServices } from '@/Services';
import inventoryAnalyticsServices from '@/Services/Analytics Services/inventory-analytics.services';

const LABELS = {
	itemSalesControl: 'Automatic menu on/off',
	itemAvailableToggleWebhook: 'Send stock levels to partners',
	orderAwareMenuToggle: 'Count promised orders',
	ingrediantLinkedParentPopulate: 'Work out recipe items & combos stock',
	ingrediantLinkedParentMenuToggle: 'Switch recipe items & combos on the menu',
	allowProductScanInDispatch: 'Allow product scan in dispatch',
	productionPlanIngStockThreshold: 'Production plan ingredient threshold',
	menuSharingCode: 'PetPooja restaurant code',
};

const notify = (type, message) => notificationServices.generateNotification({ type, message });

const StoreSettingsSection = ({ storeId }) => {
	const [rows, setRows] = useState([]);
	const [draft, setDraft] = useState({});
	const [saving, setSaving] = useState(null);

	useEffect(() => {
		if (!storeId) return;
		inventoryAnalyticsServices.fetchStoreSettings({ data: { storeId } }).then((res) => {
			if (res?.statusCode === 200) setRows(res.data);
		}).catch((e) => notify('error', e?.message || 'Could not load store settings'));
	}, [storeId]);

	const save = async (key, value) => {
		setSaving(key);
		try {
			const res = await inventoryAnalyticsServices.saveStoreSettings({ data: { storeId, changes: { [key]: value } } });
			if (res?.statusCode !== 200) throw new Error(res?.errorMessage || res?.message);
			setRows(res.data);
			setDraft((d) => ({ ...d, [key]: undefined }));
			notify('success', `${LABELS[key] || key} saved`);
		} catch (e) {
			notify('error', e?.message || 'Could not save');
		} finally {
			setSaving(null);
		}
	};

	if (!rows.length) return null;
	return (
		<div className='mx-4 my-6 border rounded-lg bg-white'>
			<div className='px-4 py-3 border-b'>
				<div className='font-semibold text-gray-900'>Settings</div>
				<div className='text-xs text-gray-500'>Each change saves straight away and records who made it.</div>
			</div>
			{rows.map((r) => {
				const last = r.lastChange;
				const draftVal = draft[r.key];
				return (
					<div key={r.key} className='flex items-center gap-4 px-4 py-3 border-b last:border-b-0'>
						<div className='flex-1 min-w-0'>
							<div className='text-sm font-medium text-gray-900'>
								{LABELS[r.key] || r.key}
								{!r.editable && <span className='ml-2 text-[10px] uppercase bg-gray-100 text-gray-600 rounded px-1.5 py-0.5'>read only</span>}
							</div>
							<div className='text-xs text-gray-500'>{r.description}</div>
							{last && (
								<div className='text-[11px] text-gray-400 mt-0.5'>
									Last changed by {last.by || 'system'} {last.at ? moment.utc(last.at).local().format('D MMM YYYY, h:mm a') : ''}
									{' '}({String(last.from ?? 'not set')} → {String(last.to ?? 'not set')})
								</div>
							)}
						</div>
						<div className='flex items-center gap-2'>
							{r.type === 'bool' && r.editable ? (
								<Switch checked={Boolean(r.value)} disabled={saving === r.key}
									onChange={(e) => {
										const on = e.target.checked;
										if (window.confirm(`${on ? 'Turn on' : 'Turn off'} "${LABELS[r.key] || r.key}" for this store?`)) save(r.key, on);
									}} />
							) : r.editable ? (
								<>
									<input
										className='border rounded px-2 py-1 text-sm w-40'
										type={r.type === 'number' ? 'number' : 'text'}
										value={draftVal ?? r.value ?? ''}
										onChange={(e) => setDraft((d) => ({ ...d, [r.key]: e.target.value }))}
									/>
									<button type='button' disabled={draftVal === undefined || saving === r.key}
										className='px-3 py-1 rounded bg-uy-color text-white text-sm disabled:opacity-40'
										onClick={() => save(r.key, draftVal === '' ? null : r.type === 'number' ? Number(draftVal) : draftVal)}>
										Save
									</button>
								</>
							) : (
								<span className='text-sm text-gray-700'>{JSON.stringify(r.value)}</span>
							)}
						</div>
					</div>
				);
			})}
		</div>
	);
};

export default StoreSettingsSection;
